import { toast } from "react-toastify";
import { apiURL } from "../../config";
import { editOverviewTab } from "./tabs";
import { addAttachment, addAttachmentFromTempFile } from "./attachments";

export async function getDecisionsList(axiosPrivate, id = false) {
	return axiosPrivate
		.get(`${apiURL}/decision/receiver/list.json${id ? "?exclude=" + id : ""}`, {})
		.then((res) => {
			if (res.status < 300 && res.status > 199) return res.data;
		})
		.catch((err) => {
			return err.response;
		});
}

export async function getDecisionsListByCategory(axiosPrivate, categories, status, exclude, limit) {
	let idsString = "";
	categories.map((e, i) => (idsString += `${i > 0 ? "," : ""}${e}`));
	let excludeIdsString = "";
	exclude.map((e, i) => (excludeIdsString += `${i > 0 ? "," : ""}${e}`));

	return axiosPrivate
		.get(
			`${apiURL}/decision/receiver/get-by-categories.json?categories=${idsString}&status=${status}&limit=${limit}&exclude=${excludeIdsString}`,
			{}
		)
		.then((res) => {
			if (res.status < 300 && res.status > 199) {
				const resDecisions = [];
				res.data.map((e) => e.decisions.map((f) => resDecisions.push(f)));

				return resDecisions;
			}
		})
		.catch((err) => {
			return err.response;
		});
}

export async function getDecisionsListByLabel(axiosPrivate, labels, status, exclude, limit) {
	let idsString = "";
	labels.map((e, i) => (idsString += `${i > 0 ? "," : ""}${e}`));
	let excludeIdsString = "";
	exclude.map((e, i) => (excludeIdsString += `${i > 0 ? "," : ""}${e}`));

	return axiosPrivate
		.get(
			`${apiURL}/decision/receiver/get-by-labels.json?labels=${idsString}&status=${status}&limit=${limit}&exclude=${excludeIdsString}`,
			{}
		)
		.then((res) => {
			if (res.status < 300 && res.status > 199) {
				const resDecisions = [];
				res.data.map((e) => e.decisions.map((f) => resDecisions.push(f)));

				return resDecisions;
			}
		})
		.catch((err) => {
			return err.response;
		});
}

export async function getDecision(axiosPrivate, id) {
	return axiosPrivate
		.get(`${apiURL}/decision/receiver/${id}.json`)
		.then((res) => {
			if (res.status < 300 && res.status > 199) return res.data;
		})
		.catch((err) => {
			return err.response;
		});
}

export async function addDecision(axiosPrivate, navigate, data) {
	return axiosPrivate
		.post(`${apiURL}/decision/maker/decision.json`, data)
		.then(async (res) => {
			if (res.status < 300 && res.status > 199) {
				// Check if we have prefilled data with overview content or attachments
				let shouldSaveOverview = false;
				let shouldSaveAttachment = false;
				
				try {
					const { PrefillDecisionManager } = await import("../../utils/PrefillDecisionManager");
					const prefillData = PrefillDecisionManager.load();
					
					console.log("🔍 Loaded prefill data:", prefillData);
					
					// Check if we need to save overview content
					if (prefillData && (prefillData.description || prefillData.summary)) {
						shouldSaveOverview = true;
						
						// Save the overview tab content
						const overviewTabData = {
							basisDescription: prefillData.description || "",
							decisionDescription: prefillData.summary || ""
						};
						
						console.log("💾 Saving overview tab data:", overviewTabData);
						console.log("📋 Decision ID:", res.data.id);
						
						try {
							await editOverviewTab(axiosPrivate, () => {
								console.log("✅ Overview tab saved successfully");
							}, res.data.id, overviewTabData);
							console.log("🔄 Overview tab operation completed");
						} catch (overviewError) {
							console.error("❌ Failed to save overview tab:", overviewError);
						}
					} else {
						console.warn("⚠️ No overview data found in prefill data");
					}
					
					// Check if we need to save attachment
					if (prefillData && prefillData.metadata && prefillData.metadata.sourceFiles && prefillData.metadata.sourceFiles.length > 0) {
						const sourceFile = prefillData.metadata.sourceFiles[0];
						
						console.log("📎 Found source file in prefill data:", sourceFile.name);
						
						// Check if we have temp file IDs from the new upload system
						const tempFileIds = prefillData.metadata.tempFileIds || [];
						
						if (tempFileIds.length > 0) {
							shouldSaveAttachment = true;
							
							try {
								console.log("� Converting temp file to attachment:", sourceFile.name);
								
								// Use the new temp file to attachment conversion
								await addAttachmentFromTempFile(
									axiosPrivate, 
									res.data.id, 
									tempFileIds[0], // Use the first temp file ID
									sourceFile.name,
									`Original document: ${sourceFile.name}`
								);
								
								console.log("✅ Successfully converted temp file to attachment");
								console.log("🔄 Attachment operation completed");
								
							} catch (attachmentError) {
								console.error("❌ Failed to convert temp file to attachment:", attachmentError);
							}
						} else {
							// Fallback to old file storage method if no temp file IDs
							console.log("🔍 No temp file IDs found, checking for legacy file reference");
							console.log("�🔗 Checking for temp file reference:", !!window.tempUploadedFile);
							
							// Check if we have the temp file reference from DocumentUploadModal
							// First try the immediate window reference, then check if we have stored file data
							let fileToUpload = window.tempUploadedFile;
							
							if (!fileToUpload && window.storedFileData) {
								console.log("🔍 Attempting to restore file from stored data");
								try {
									// Try to recreate File from stored data if it exists
									const blob = new Blob([window.storedFileData.content], { type: window.storedFileData.type });
									fileToUpload = new File([blob], window.storedFileData.name, {
										type: window.storedFileData.type,
										lastModified: window.storedFileData.lastModified
									});
									console.log("✅ File restored from stored data:", fileToUpload.name);
								} catch (restoreError) {
									console.warn("❌ Failed to restore file from stored data:", restoreError);
								}
							}
							
							if (fileToUpload) {
								shouldSaveAttachment = true;
								
								try {
									console.log("📎 Saving uploaded file as attachment:", sourceFile.name);
									
									// Create FormData for the attachment
									const attachmentData = new FormData();
									attachmentData.append("name", sourceFile.name);
									attachmentData.append("description", `Original document: ${sourceFile.name}`);
									attachmentData.append("file", fileToUpload);
									
									// Save attachment and wait for completion
									await addAttachment(axiosPrivate, res.data.id, () => {
										console.log("✅ Successfully saved attachment");
									}, attachmentData);
									
									console.log("🔄 Attachment operation completed");
									
									// Clean up temp file references only after successful save
									delete window.tempUploadedFile;
									delete window.storedFileData;
									console.log("🧹 Cleaned up temp file references");
								} catch (attachmentError) {
									console.error("❌ Failed to save attachment:", attachmentError);
								}
							} else {
								console.warn("⚠️ No temp file reference found");
								console.log("🔍 window.tempUploadedFile:", !!window.tempUploadedFile);
								console.log("🔍 window.storedFileData:", !!window.storedFileData);
								console.log("🔍 Available sessionStorage keys:", Object.keys(sessionStorage));
							}
						}
					} else {
						console.warn("⚠️ No source files found in prefill data");
					}
					
					// Clean up prefill data only after all operations complete
					if (shouldSaveOverview || shouldSaveAttachment) {
						// Add a small delay to ensure operations are complete
						setTimeout(() => {
							try {
								PrefillDecisionManager.clear();
								console.log("🧹 Cleaned up prefill data after decision creation");
							} catch (cleanupError) {
								console.warn("⚠️ Failed to cleanup prefill data:", cleanupError);
							}
						}, 1000);
					} else {
						PrefillDecisionManager.clear();
						console.log("🧹 Cleaned up prefill data (no additional operations)");
					}
				} catch (error) {
					console.error("Error handling prefill data:", error);
					// Still clean up if error occurs
					try {
						const { PrefillDecisionManager } = await import("../../utils/PrefillDecisionManager");
						PrefillDecisionManager.clear();
					} catch (cleanupError) {
						console.warn("Failed to cleanup after error:", cleanupError);
					}
				}
				
				console.log("🚀 All operations completed, navigating to decision page...");
				toast.success("Decision added");
				
				// Add a longer delay to ensure all server operations complete and data is persisted
				setTimeout(() => {
					navigate(`/decisions/${res.data.id}`, { replace: true });
				}, 2000); // Increased to 2 seconds
			} else {
				toast.error("Could not add decision. Please try again.");
			}
			return res;
		})
		.catch((err) => {
			console.error("=== DECISION CREATION ERROR ===");
			console.error("Status:", err.response?.status);
			console.error("Status Text:", err.response?.statusText);
			console.error("Error Response Data:", err.response?.data);
			
			// Log field errors specifically
			if (err.response?.data?.fieldErrors) {
				console.error("=== FIELD VALIDATION ERRORS ===");
				err.response.data.fieldErrors.forEach((fieldError, index) => {
					console.error(`Field Error ${index + 1}:`, fieldError);
				});
				console.error("================================");
			}
			
			console.error("Request URL:", err.config?.url);
			console.error("Request Method:", err.config?.method);
			console.error("Sent Data:", data);
			console.error("Full Error Object:", err);
			console.error("================================");
			
			// Show specific error message if available
			if (err.response?.data?.fieldErrors && err.response.data.fieldErrors.length > 0) {
				const fieldErrorMessages = err.response.data.fieldErrors.map(fieldErr => fieldErr.message || fieldErr).join(", ");
				toast.error(`Validation failed: ${fieldErrorMessages}`);
			} else if (err.response?.data?.message) {
				toast.error(`Could not add decision: ${err.response.data.message}`);
			} else if (err.response?.data?.error) {
				toast.error(`Could not add decision: ${err.response.data.error}`);
			} else if (err.response?.data && typeof err.response.data === "string") {
				toast.error(`Could not add decision: ${err.response.data}`);
			} else if (err.response?.status === 400) {
				toast.error("Could not add decision: invalid data. Check the console for details.");
			} else {
				toast.error("Could not add decision. Please try again.");
			}
			
			return err.response;
		});
}

export async function editDecision(axiosPrivate, cb, id, data) {
	return axiosPrivate
		.patch(`${apiURL}/decision/maker/edit/${parseInt(id)}.json`, data, {
			headers: { "Content-Type": "application/json" },
		})
		.then((res) => {
			if (res.status < 300 && res.status > 199) {
				toast.success("Decision updated");
				cb();
			} else {
				toast.error("Could not update decision. Please try again.");
			}
			return res;
		})
		.catch((err) => {
			return err.response;
		});
}

export async function deleteDecisionUrl(axiosPrivate, cb, id) {
	return axiosPrivate
		.patch(
			`${apiURL}/decision/maker/overview-tab/${parseInt(id)}.json`,
			{ url: null },
			{
				headers: { "Content-Type": "application/json" },
			}
		)
		.then((res) => {
			if (res.status < 300 && res.status > 199) {
				toast.success("Document link removed");
				cb();
			} else {
				toast.error("Could not remove document link. Please try again.");
			}
			return res;
		})
		.catch((err) => {
			return err.response;
		});
}

export async function addDecisionUrl(axiosPrivate, id, url, cb) {
	return axiosPrivate
		.patch(
			`${apiURL}/decision/maker/overview-tab/${parseInt(id)}.json`,
			{ url: url },
			{
				headers: { "Content-Type": "application/json" },
			}
		)
		.then((res) => {
			if (res.status < 300 && res.status > 199) {
				toast.success("Document link added");
				cb();
			} else {
				toast.error("Could not add document link. Please try again.");
			}
			return res;
		})
		.catch((err) => {
			return err.response;
		});
}

export const getDecisionChunks = async (axiosPrivate, id) => {
	return axiosPrivate
		.get(`${apiURL}/decision/${parseInt(id)}/chunks.json`, {})
		.then((res) => {
			if (res.status < 300 && res.status > 199) return res.data;
		})
		.catch((err) => {
			return err.response;
		});
};

export const postDecisionChunk = async (axiosPrivate, id, data, cb) => {
	return axiosPrivate
		.post(`${apiURL}/decision/${parseInt(id)}/chunk.json`, data, {
			headers: { "Content-Type": "application/json" },
		})
		.then((res) => {
			if (res.status < 300 && res.status > 199) {
				toast.success("Addressee part added");
				cb();
			} else {
				toast.error("Could not add addressee part. Please try again.");
			}
			return res;
		})
		.catch((err) => {
			return err.response;
		});
};

export const putDecisionChunk = async (axiosPrivate, decisionId, id, data, cb) => {
	return axiosPrivate
		.put(`${apiURL}/decision/${parseInt(decisionId)}/chunk/${id}.json`, data, {
			headers: { "Content-Type": "application/json" },
		})
		.then((res) => {
			if (res.status < 300 && res.status > 199) {
				toast.success("Addressee part updated");
				cb();
			} else {
				toast.error("Could not update addressee part. Please try again.");
			}
			return res;
		})
		.catch((err) => {
			return err.response;
		});
};

export const deleteDecisionChunk = async (axiosPrivate, decisionId, id, cb) => {
	return axiosPrivate
		.delete(`${apiURL}/decision/${parseInt(decisionId)}/chunk/${id}.json`)
		.then((res) => {
			if (res.status > 199 && res.status < 300) {
				toast.success("Addressee part deleted");
				cb();
			} else {
				toast.error("Could not delete addressee part. Please try again.");
			}
		})
		.catch((err) => {
			return err.response;
		});
};

export const reorderDecisionChunks = async (axiosPrivate, decisionId, chunks) => {
	return axiosPrivate
		.put(`${apiURL}/decision/${parseInt(decisionId)}/chunks/reorder.json`, chunks, {
			headers: { "Content-Type": "application/json" },
		})
		.then((res) => {
			if (res.status < 300 && res.status > 199) {
				return res.data;
			} else {
				toast.error("Could not reorder addressee parts. Please try again.");
			}
		})
		.catch((err) => {
			toast.error("Could not reorder addressee parts. Please try again.");
			console.error("Reorder error:", err);
			return err.response;
		});
};